import { useState } from "react";
import useAuthState from "../hooks/useAuthState";
import Logo from "../images/transparent128x128.png";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { Auth } from "aws-amplify";
import { makeStyles } from "@material-ui/core/styles";
import FScreen from "../components/Screen";
import useUIState from "../hooks/useUIstate";
import { MdArrowBack } from "react-icons/md";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
      width: 375,
    },
    "& label": {
      marginLeft: theme.spacing(1),
      marginBottom: "0",
      fontSize: "0.8rem",
      textTransform: "uppercase",
    },
    "& .MuiButtonBase-root ": {
      margin: theme.spacing(1),
      width: "auto",
      padding: "9px 14px",
      marginTop: "12px",
    },
  },
}));

function LoginScreen() {
  const classes = useStyles();
  const [LoginMode, setLoginMode] = useState("Login");
  const history = useHistory();
  const { setAuthStatus, setUser, User } = useAuthState();
  const {
    setShowLoadingModal,
    setToastMessage,
    setShowToastMessage,
    setToastType,
  } = useUIState();
  const { t } = useTranslation();

  const [inputs, setInputs] = useState({
    username: "",
    password: "",
    newPassword: "",
    verificationCode: "",
  });
  const {
    username,
    password, // more than 7 char, with lowercase, uppercase and numeric
    newPassword,
    verificationCode,
  } = inputs;

  function handleChange(e) {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setShowLoadingModal(true);

    if (LoginMode === "Login") {
      try {
        const user = await Auth.signIn(username, password);
        setShowLoadingModal(false);

        if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
          setUser(user);
          setLoginMode("CompleteNewPassword");
        } else {
          // Login Successful
          const fullUser = await Auth.currentAuthenticatedUser();
          setUser(fullUser);
          setAuthStatus("loggedIn");
          localStorage.setItem("User", JSON.stringify(fullUser));
          history.push("/retail-locations");
        }
      } catch (err: any) {
        console.log("error signing in", err);
        setShowLoadingModal(false);
        let errorMessage;

        if (err.code === "UserNotFoundException") {
          errorMessage = t("loginscreen.error.unknown-username");
        } else if (err.code === "NotAuthorizedException") {
          errorMessage = t("loginscreen.error.incorrect-password-or-username");
        } else {
          errorMessage = err.Message;
        }

        setToastMessage(errorMessage);
        setShowToastMessage(true);
      }
    } else if (LoginMode === "CompleteNewPassword") {
      const userAttributes = [];
      Auth.completeNewPassword(User, newPassword, userAttributes)
        .then(async () => {
          const fullUser = await Auth.currentAuthenticatedUser();
          setAuthStatus("loggedIn");
          localStorage.setItem("User", JSON.stringify(fullUser));
          setUser(fullUser);
          setShowLoadingModal(false);
          history.push("/retail-locations");
        })
        .catch((err: any) => {
          setShowLoadingModal(false);
          let errorMessage;

          if (err.message.indexOf("not long") !== -1) {
            errorMessage = t("loginscreen.error.password-req-min-length");
          } else if (err.message.indexOf("lowercase") !== -1) {
            errorMessage = t("loginscreen.error.password-req-lowercase");
          } else if (err.message.indexOf("uppercase") !== -1) {
            errorMessage = t("loginscreen.error.password-req-uppercase");
          } else if (err.message.indexOf("symbol") !== -1) {
            errorMessage = t("loginscreen.error.password-req-special");
          } else if (err.message.indexOf("numeric") !== -1) {
            errorMessage = t("loginscreen.error.password-req-number");
          } else {
            errorMessage = err.Message;
          }
          setToastMessage(errorMessage);
          setShowToastMessage(true);
        });
    } else if (LoginMode === "ForgotPassword") {
      Auth.forgotPassword(username)
        .then((user) => {
          // show success message
          setLoginMode("ForgotPasswordVerificationCode");
          setShowLoadingModal(false);
        })
        .catch((err) => {
          console.log(err);
          setShowLoadingModal(false);

          let errorMessage;

          if (err.message.indexOf("Username") !== -1) {
            errorMessage = t("loginscreen.error.unknown-username");
          } else {
            errorMessage = err.message;
          }

          setToastMessage(errorMessage);
          setShowToastMessage(true);
        });
    } else if (LoginMode === "ForgotPasswordVerificationCode") {
      Auth.forgotPasswordSubmit(username, verificationCode, newPassword)
        .then(() => {
          setShowLoadingModal(false);
          setToastType("success");
          setToastMessage(t("loginscreen.password-reset-success"));
          setShowToastMessage(true);
          setLoginMode("Login");
        })
        .catch((err: any) => {
          console.log(err);
          setShowLoadingModal(false);

          let errorMessage;

          if (err.message.indexOf("Username/client") !== -1) {
            errorMessage = t("loginscreen.error.unknown-username");
          } else if (err.message.indexOf("Invalid code") !== -1) {
            errorMessage = t("loginscreen.error.wrong-verificationcode");
          } else if (err.message.indexOf("verification code") !== -1) {
            errorMessage = t("loginscreen.error.wrong-verificationcode");
          } else if (err.message.indexOf("validation errors") !== -1) {
            errorMessage = t("loginscreen.error.password-req-generic");
          } else if (err.message.indexOf("1 validation error") !== -1) {
            errorMessage = t("loginscreen.error");
          } else if (err.message.indexOf("not long") !== -1) {
            errorMessage = t("loginscreen.error.password-req-min-length");
          } else if (err.message.indexOf("lowercase") !== -1) {
            errorMessage = t("loginscreen.error.password-req-lowercase");
          } else if (err.message.indexOf("uppercase") !== -1) {
            errorMessage = t("loginscreen.error.password-req-uppercase");
          } else if (err.message.indexOf("symbol") !== -1) {
            errorMessage = t("loginscreen.error.password-req-special");
          } else if (err.message.indexOf("numeric") !== -1) {
            errorMessage = t("loginscreen.error.password-req-number");
          } else {
            errorMessage = err.message;
          }

          setToastType("error");
          setToastMessage(errorMessage);
          setShowToastMessage(true);
        });
    }
  }

  function signOut() {
    Auth.signOut()
      .then((data) => {
        setLoginMode("Login");
        localStorage.clear();
        setAuthStatus("noUser");
      })
      .catch((err) => console.log(err));
  }

  return (
    <FScreen>
      <div className="flex-col" style={{ alignItems: "center" }}>
        {/* NORMAL LOGIN */}
        {LoginMode === "Login" ? (
          <>
            <img src={Logo} width="100" alt="logo" className="logo" />
            <h1 className="text-center">{t("loginscreen.login")}</h1>
            <h4 style={{ marginTop: 0 }}>
              {t("loginscreen.input-authentication")}
            </h4>
            <form
              className={classes.root}
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "10px",
              }}
              onSubmit={handleSubmit}
            >
              <label htmlFor="username">{t("loginscreen.username")}</label>
              <TextField
                id="username"
                name="username"
                type="email"
                required
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
              <label htmlFor="username">{t("loginscreen.password")}</label>
              <TextField
                id="password"
                name="password"
                type="password"
                required
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
              <Button type="submit" variant="contained" color="primary">
                {t("loginscreen.login").toUpperCase()}
              </Button>
              <div
                className="mt-1 pointer text-center"
                onClick={() => setLoginMode("ForgotPassword")}
              >
                {t("loginscreen.forgot-password")}
              </div>
            </form>
          </>
        ) : null}

        {/* COMPLETE NEW PASSWORD */}
        {LoginMode === "CompleteNewPassword" ? (
          <>
            <img src={Logo} width="100" alt="logo" className="logo" />
            <h1 className="text-center">{t("loginscreen.change-password")}</h1>
            <div className="text-center">
              {t("loginscreen.choose-new-password")}
              <br />
              <div
                dangerouslySetInnerHTML={{
                  __html: t(
                    "loginscreen.password-policy-description",
                  ) as string,
                }}
              />
            </div>
            <form
              className={classes.root}
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "20px",
              }}
              onSubmit={handleSubmit}
            >
              <label htmlFor="username">{t("loginscreen.new-password")}</label>
              <TextField
                id="newPassword"
                name="newPassword"
                type="password"
                required
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
              <div className="flex-center-align jcsb">
                <Button className="pointer" onClick={() => signOut()}>
                  <MdArrowBack size="20" />
                  {t("loginscreen.return-to-login")}
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  {t("loginscreen.alter")}
                </Button>
              </div>
            </form>
          </>
        ) : null}

        {/* FORGOT PASSWORD */}
        {LoginMode === "ForgotPassword" ? (
          <>
            <img src={Logo} width="100" alt="logo" className="logo" />
            <h1 className="text-center">{t("loginscreen.forgot-password")}</h1>
            <p className="text-center">
              <div
                dangerouslySetInnerHTML={{
                  __html: t("loginscreen.reset-instruction1") as string,
                }}
              />
            </p>
            <p className="text-center">{t("loginscreen.message-check-spam")}</p>
            <form
              className={classes.root}
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "20px",
              }}
              onSubmit={handleSubmit}
            >
              <label htmlFor="username">{t("loginscreen.email")}</label>
              <TextField
                id="username"
                name="username"
                type="email"
                required
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
              <div className="flex-center-align jcsb">
                <Button
                  className="pointer"
                  onClick={() => {
                    setLoginMode("Login");
                  }}
                >
                  <MdArrowBack size="20" />
                  {t("loginscreen.return-to-login")}
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  {t("loginscreen.send")}
                </Button>
              </div>
              {/* <Link className="link text-center" to="/forgot-password">Wachtwoord vergeten?</Link> */}
            </form>
          </>
        ) : null}

        {/* FORGOT PASSWORD /w VERIFICATION CODE */}
        {LoginMode === "ForgotPasswordVerificationCode" ? (
          <>
            <img src={Logo} width="100" alt="logo" className="logo" />
            <h1 className="text-center">{t("loginscreen.forgot-password")}</h1>
            <p className="text-center">{t("loginscreen.reset-instruction2")}</p>
            <div
              className="text-center"
              dangerouslySetInnerHTML={{
                __html: t("loginscreen.new-password-instruction") as string,
              }}
            />
            <form
              className={classes.root}
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "20px",
              }}
              onSubmit={handleSubmit}
            >
              <label htmlFor="username">{t("loginscreen.email")}</label>
              <TextField
                id="username"
                name="username"
                type="email"
                required
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
              <label htmlFor="username">
                {t("loginscreen.verification-code")}
              </label>
              <TextField
                id="verificationCode"
                name="verificationCode"
                type="password"
                required
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
              <label htmlFor="username">{t("loginscreen.new-password")}</label>
              <TextField
                id="newPassword"
                name="newPassword"
                type="password"
                required
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
              <div className="flex-center-align jcsb">
                <Button
                  className="pointer"
                  onClick={() => {
                    setLoginMode("Login");
                  }}
                >
                  <MdArrowBack size="20" />
                  {t("loginscreen.return-to-login")}
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  {t("loginscreen.alter")}
                </Button>
              </div>
            </form>
          </>
        ) : null}
      </div>
    </FScreen>
  );
}

export default LoginScreen;
